import { useEffect, useRef, useState } from "react";
import { STAGES, STAGE_COUNT } from "../stages";
import { getStageState } from "../lib/journey";
import { callAI } from "../lib/api";
import { chatSystem, navigationTools, runTools } from "../lib/askExpert";
import { renderMD } from "../lib/markdown";

const SUGGESTIONS = ["What's happening on this step?", "Move to the next step", "Regenerate this"];

// Pure conversational chat — no stepper, no stage-card chrome, no list of
// prior stages. The current stage's own Component still mounts (so its
// auto-run-on-entry generation still fires and writes into journey state),
// but presented as plain content in the feed rather than a titled/bordered
// card — reads as part of the conversation with Cisero, not a separate UI.
export default function ChatJourney({ journey, currentIdx, onBack, onAdvance, onJump, onUpdate, onUpdateStage, advanceDisabled }) {
  const endRef = useRef(null);
  const stageBodyRef = useRef(null);
  const prevIdxRef = useRef(currentIdx);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  // New chat activity scrolls to the bottom of the feed. A stage change
  // (jump/advance/back) scrolls the stage body itself into view instead —
  // it renders above the accumulated messages, so once a conversation has
  // enough turns, "scroll to bottom" alone hides it above the fold and a
  // jump looks like it silently did nothing even though it worked. A jump
  // and its confirmation message land in the same update, so this has to
  // pick one winner rather than run both effects independently.
  useEffect(() => {
    if (currentIdx !== prevIdxRef.current) {
      prevIdxRef.current = currentIdx;
      stageBodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [currentIdx, messages.length, sending]);

  const currentStage = STAGES[currentIdx];
  const currentState = getStageState(journey, currentStage.id);
  const CurrentBody = currentStage.Component;

  const doSend = async (text) => {
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      // Send the model only what it actually said (apiContent), never the
      // "→ Jumped to X." confirmation our own code appends for display —
      // otherwise the model sees its own past turns "succeed" via plain
      // text and starts mimicking that phrase instead of calling the tool.
      // Also cap how much history goes back — the model's own reply is
      // almost always a bare "Done." even when it *did* call a tool (no
      // structured tool_result is preserved here), so a long accumulation
      // of "Done." turns eventually reads as "acknowledge, don't act."
      // Trim by whole user/assistant pairs (messages is always even-length,
      // strictly alternating starting on "user") so the API-bound history
      // never starts mid-pair.
      const HISTORY_PAIRS = 4;
      const apiHistory = [...messages.slice(-HISTORY_PAIRS * 2), { role: "user", content: text }]
        .map((m) => ({ role: m.role, content: m.apiContent ?? m.content }));
      const { text: reply, toolCalls } = await callAI({
        system: chatSystem(currentStage, journey),
        messages: apiHistory,
        tools: navigationTools,
        maxTokens: 1000,
        temperature: 0,
      });
      const actionNote = runTools(toolCalls, { onAdvance, onBack, onJump, onUpdateStage });
      const content = [reply, actionNote].filter(Boolean).join("\n\n");
      setMessages((m) => [...m, { role: "assistant", content: content || "Done.", apiContent: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Connection error — try again." }]);
    }
    setSending(false);
  };

  const send = () => doSend(input.trim());

  const onInputKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="s-chat-wrap">
      <div className="s-chat-scroll">
        <div className="s-chat-msg assistant">{currentStage.blurb}</div>

        <div className="s-chat-body" style={{ margin: 0 }} ref={stageBodyRef}>
          <CurrentBody state={currentState} journey={journey} update={onUpdate} />
        </div>

        {messages.map((m, i) => (
          <div key={i} className={`s-chat-msg ${m.role}`} dangerouslySetInnerHTML={{ __html: renderMD(m.content) }} />
        ))}
        {sending && <div className="s-chat-msg assistant s-chat-msg-loading">…</div>}

        <div ref={endRef} />
      </div>

      <div className="s-chat-suggestions">
        {SUGGESTIONS.map((s) => (
          <button type="button" key={s} className="s-chat-pill" onClick={() => doSend(s)} disabled={sending}>{s}</button>
        ))}
      </div>

      <div className="s-chat-input-row">
        <input
          className="s-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKey}
          placeholder="Type a question or ask Cisero anything…"
          disabled={sending}
        />
        <button type="button" className="s-chat-send" onClick={send} disabled={sending || !input.trim()}>Send</button>
      </div>
    </div>
  );
}
